import { useState, useEffect } from 'react';
import { useLang } from '../../../context/LanguageContext';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t('Servicii', 'Услуги'), href: '#services' },
    { label: t('Mărci', 'Бренды'), href: '#brands' },
    { label: t('Cum Funcționează', 'Как работает'), href: '#how-it-works' },
    { label: t('De Ce Noi', 'Почему мы'), href: '#why-us' },
    { label: t('Contact', 'Контакты'), href: '#footer' },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  /* Single-click toggle: clicking anywhere on the pill switches language */
  const LangToggle = ({ className = '' }: { className?: string }) => (
    <button
      onClick={() => setLang(lang === 'ro' ? 'ru' : 'ro')}
      className={`flex items-center rounded-full border border-neon/30 overflow-hidden bg-charcoal-100 cursor-pointer select-none ${className}`}
      aria-label={t('Schimbă limba', 'Сменить язык')}
    >
      <span
        className={`px-3 py-1.5 text-xs font-rajdhani font-bold tracking-widest transition-all duration-200 ${
          lang === 'ro' ? 'bg-neon text-charcoal' : 'text-cyber-muted'
        }`}
      >
        RO
      </span>
      <span
        className={`px-3 py-1.5 text-xs font-rajdhani font-bold tracking-widest transition-all duration-200 ${
          lang === 'ru' ? 'bg-neon text-charcoal' : 'text-cyber-muted'
        }`}
      >
        RU
      </span>
    </button>
  );

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-charcoal/96 backdrop-blur-md border-b border-neon/10 shadow-[0_4px_30px_rgba(0,255,136,0.05)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 sm:py-4 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          className="flex items-center select-none cursor-pointer"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          <span className="font-rajdhani font-bold text-2xl tracking-wide">
            <span className="text-white">My</span>
            <span className="text-neon" style={{ textShadow: '0 0 12px rgba(0,255,136,0.6)' }}>Alarm</span>
          </span>
          <span className="ml-2 w-2 h-2 rounded-full bg-neon animate-pulse flex-shrink-0"></span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="font-inter text-sm text-cyber-muted hover:text-neon transition-colors duration-200 whitespace-nowrap cursor-pointer relative group"
            >
              {link.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-neon group-hover:w-full transition-all duration-300"></span>
            </a>
          ))}
        </div>

        {/* Right side: Lang toggle + CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <LangToggle />
          <a
            href="https://wa.me/37360123456"
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-neon text-charcoal font-rajdhani font-bold text-sm btn-neon transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-base"></i>
            {t('Deviz Gratuit', 'Бесплатная смета')}
          </a>
        </div>

        {/* Mobile: lang toggle + hamburger */}
        <div className="lg:hidden flex items-center gap-3">
          <LangToggle />
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-10 h-10 flex items-center justify-center text-neon cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-2xl transition-all duration-200 ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-charcoal/98 backdrop-blur-md border-t border-neon/10 overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 sm:px-6 py-3">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="flex items-center gap-3 font-inter text-base text-cyber-text hover:text-neon transition-colors py-4 border-b border-charcoal-300 cursor-pointer"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-neon/50 flex-shrink-0"></span>
              {link.label}
            </a>
          ))}
          <a
            href="https://wa.me/37360123456?text=Buna%20ziua!%20Doresc%20un%20deviz%20gratuit."
            target="_blank"
            rel="nofollow noreferrer"
            className="mt-5 mb-2 flex items-center justify-center gap-2.5 px-5 py-4 rounded-md bg-neon text-charcoal font-rajdhani font-bold text-base cursor-pointer"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            {t('Cere Deviz Gratuit', 'Запросить смету')}
          </a>
        </div>
      </div>
    </nav>
  );
}
